import { useState } from "react";
import { FileSignature, Download, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";

/**
 * Organizer-only: one-tap AI-drafted vendor agreement PDF download.
 * First tap can take 30-45s (Claude drafts + reportlab renders); subsequent
 * taps hit a server-side cache and return instantly. If a specific vendor is
 * passed, the contract is personalized to them, otherwise it's the
 * event-wide template.
 */
export default function AIContractButton({ eventId, vendorId = null,
                                            vendorName = null, compact = false }) {
  const [busy, setBusy] = useState(false);

  const download = async () => {
    if (busy) return;
    setBusy(true);
    const t = toast.loading(
      vendorName
        ? `Drafting contract for ${vendorName}… first draft takes about 30 seconds.`
        : "Drafting your vendor agreement… first draft takes about 30 seconds.",
    );
    try {
      const params = new URLSearchParams();
      if (vendorId) params.set("vendor_id", vendorId);
      const url = `/ai/contracts/${eventId}${params.toString() ? `?${params}` : ""}`;
      const res = await api.get(url, { responseType: "blob", timeout: 60000 });
      // Filename from Content-Disposition header, or a sane default.
      const cd = res.headers["content-disposition"] || "";
      const m = cd.match(/filename="?([^";]+)"?/i);
      const filename = m ? m[1] : `${vendorName ? vendorName.toLowerCase().replace(/\s+/g, "-") + "-" : ""}vendor-agreement.pdf`;
      // Trigger a browser download.
      const blob = new Blob([res.data], { type: "application/pdf" });
      const objectUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(objectUrl);
      const cached = res.headers["x-contract-cached"] === "1";
      toast.success(cached ? "Ready — downloaded your cached contract." : "Contract ready — check your downloads.",
                    { id: t });
    } catch (e) {
      // Blob responses swallow the JSON detail — try to read it back if present.
      let detail = "";
      if (e.response?.data instanceof Blob) {
        try {
          const asText = await e.response.data.text();
          try { detail = JSON.parse(asText)?.detail || ""; }
          catch { detail = asText.slice(0, 240); }
        } catch { /* ignore */ }
      } else {
        detail = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      }
      toast.error(detail || "Could not generate contract. Please try again.", { id: t });
    } finally {
      setBusy(false);
    }
  };

  if (compact) {
    return (
      <button
        type="button"
        onClick={download}
        disabled={busy}
        data-testid={`contract-btn-${vendorId || "event"}`}
        title="Download AI-drafted vendor agreement"
        className="text-xs inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border whitespace-nowrap"
        style={{ borderColor: "rgba(0,0,0,0.15)", backgroundColor: "#fff" }}
      >
        {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <FileSignature className="h-3 w-3" />}
        {busy ? "Drafting…" : "Contract"}
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={download}
      disabled={busy}
      data-testid={`contract-btn-${vendorId || "event"}-full`}
      className="btn-outline rounded-full px-5 py-2.5 inline-flex items-center gap-2 whitespace-nowrap"
    >
      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
      {busy ? "Drafting contract…" : vendorName ? `Contract for ${vendorName}` : "Vendor agreement (PDF)"}
    </button>
  );
}
